import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/utils/dateFormat";
import { Printer, ArrowLeft, Target, Shield, Clock, MapPin } from "lucide-react";

const API = "/api";

function parseDT(s) {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDayHeading(d) {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeShort(dt) {
  const d = parseDT(dt);
  if (!d) return "TBD";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function PrintSchedule() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}?t=${Date.now()}`);
        setEvent(res.data);
      } catch (e) {
        console.error("Failed to load event", e);
      } finally {
        setLoading(false);
      }
    };
    if (eventId) load();
  }, [eventId]);

  // Group matches by date
  const grouped = useMemo(() => {
    const matches = event?.matches || [];
    const groups = new Map();
    const noTime = [];
    matches.forEach((m) => {
      const d = parseDT(m.start_time);
      if (!d) {
        noTime.push(m);
        return;
      }
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (!groups.has(key)) groups.set(key, { date: d, items: [] });
      groups.get(key).items.push(m);
    });
    // sort each group by time
    groups.forEach((g) => {
      g.items.sort((a, b) => (parseDT(a.start_time)?.getTime() ?? 0) - (parseDT(b.start_time)?.getTime() ?? 0));
    });
    const sorted = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
    return { sorted, noTime };
  }, [event]);

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading schedule…</div>;
  }
  if (!event) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-700">Event not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      </div>
    );
  }

  const orgName = settings.organization_name || "Trap Tracker";
  const totalMatches = (event.matches || []).length;

  return (
    <div data-testid="print-schedule-page" className="print-schedule">
      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter portrait; }
          html, body { background: white !important; }
          .no-print { display: none !important; }
          .print-schedule { padding: 0 !important; }
          .ps-day { break-inside: avoid; page-break-inside: avoid; }
          .ps-row { break-inside: avoid; page-break-inside: avoid; }
          a { color: inherit; text-decoration: none; }
        }
        .ps-table-row:nth-child(even) { background: #fafaf7; }
      `}</style>

      {/* On-screen toolbar (hidden when printing) */}
      <div className="no-print mb-6 flex items-center justify-between gap-3 print:hidden">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="print-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <Button
          onClick={() => window.print()}
          className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
          data-testid="print-now-btn"
        >
          <Printer size={16} className="mr-2" />
          Print Schedule
        </Button>
      </div>

      {/* Printable content */}
      <div className="max-w-4xl mx-auto bg-white text-slate-900 p-6 sm:p-8 print:p-0">
        {/* Header */}
        <header className="border-b-2 border-slate-900 pb-4 mb-6 flex items-start justify-between gap-6">
          <div>
            {settings.logo_url && (
              <img
                src={settings.logo_url}
                alt={orgName}
                className="h-16 w-auto mb-2 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <p className="text-xs uppercase tracking-wider text-slate-500">{orgName}</p>
            <h1 className="font-serif text-3xl font-bold mt-1">{event.name}</h1>
            {event.description && (
              <p className="text-sm text-slate-700 mt-1 max-w-xl">{event.description}</p>
            )}
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold text-slate-900">Match Schedule</p>
            <p className="text-slate-700 mt-1">
              {formatDate(event.start_date)}
              {event.end_date && event.end_date !== event.start_date ? ` – ${formatDate(event.end_date)}` : ""}
            </p>
            <p className="text-xs text-slate-500 mt-1">{totalMatches} matches</p>
          </div>
        </header>

        {/* Schedule body */}
        {totalMatches === 0 ? (
          <p className="text-center text-slate-500 py-12">No matches scheduled for this event.</p>
        ) : (
          <div className="space-y-6">
            {grouped.sorted.map(([key, g]) => (
              <section key={key} className="ps-day">
                <h2 className="font-serif text-xl font-semibold border-b border-slate-300 pb-1 mb-2">
                  {formatDayHeading(g.date)}
                </h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 text-xs uppercase">
                      <th className="py-1 pr-2 w-24">Time</th>
                      <th className="py-1 pr-2">Match</th>
                      <th className="py-1 pr-2 w-20 text-center">Shots</th>
                      <th className="py-1 pr-2 w-20 text-center">Trap</th>
                      <th className="py-1 pr-2 w-24 text-center">Yardage</th>
                      <th className="py-1 pr-2 w-28">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((m) => (
                      <tr key={m.id} className="ps-table-row ps-row border-t border-slate-200">
                        <td className="py-2 pr-2 font-mono font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={12} className="opacity-60" />
                            {formatTimeShort(m.start_time)}
                          </span>
                        </td>
                        <td className="py-2 pr-2">
                          <span className="font-medium">{m.name}</span>
                          {m.match_number && (
                            <span className="ml-2 text-xs text-slate-500">#{m.match_number}</span>
                          )}
                          {m.is_championship && (
                            <span className="ml-2 text-xs bg-yellow-100 px-1.5 py-0.5 rounded">Aggregate</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-center font-mono">{m.shots_per_round || "-"}</td>
                        <td className="py-2 pr-2 text-center">
                          {m.trap_number ? (
                            <span className="inline-flex items-center gap-1">
                              <MapPin size={12} className="opacity-60" />
                              {m.trap_number}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-center font-mono">{m.yardage || "-"}</td>
                        <td className="py-2 pr-2">
                          {m.is_protection ? (
                            <span className="inline-flex items-center gap-1 text-purple-700">
                              <Shield size={12} />
                              Protection
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <Target size={12} />
                              Standard
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}

            {grouped.noTime.length > 0 && (
              <section className="ps-day">
                <h2 className="font-serif text-xl font-semibold border-b border-slate-300 pb-1 mb-2">
                  Time TBD
                </h2>
                <ul className="text-sm divide-y divide-slate-200">
                  {grouped.noTime.map((m) => (
                    <li key={m.id} className="py-2 flex items-center justify-between">
                      <span>
                        <span className="font-medium">{m.name}</span>
                        {m.match_number && (
                          <span className="ml-2 text-xs text-slate-500">#{m.match_number}</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500">
                        {m.shots_per_round ? `${m.shots_per_round} shots` : ""}
                        {m.trap_number ? ` • Trap ${m.trap_number}` : ""}
                        {m.yardage ? ` • ${m.yardage} yds` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-8 pt-3 border-t border-slate-300 flex items-center justify-between text-xs text-slate-500">
          <span>{orgName}</span>
          <span>Printed {new Date().toLocaleString("en-US")}</span>
        </footer>
      </div>
    </div>
  );
}
